import {
  ArrowClockwiseIcon,
  ArrowSquareOutIcon,
  CheckIcon,
  CopyIcon,
  LinkIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreviewAccount } from '@/hooks/account/usePreviewAccount';
import {
  bioLinkUrl,
  useCreatePostDiscountCode,
  usePinBioLink,
  usePostAttribution,
  usePostAttributionPreflight,
  useReconcilePostSales,
} from '@/hooks/analytics/usePostAttribution';
import {
  buildCheckedSentence,
  buildProvenanceCopy,
  buildTapSentence,
} from '@/lib/attribution/format';
import {
  ANALYTICS_CARD,
  ANALYTICS_CARD_BODY,
  ANALYTICS_CARD_HEADER,
  SELECTABLE_PILL,
  SELECTABLE_PILL_OFF,
  SELECTABLE_PILL_ON,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * "Sales from this post" — the only place in Mora where a single post is tied to
 * real money, and the only place that says HOW.
 *
 * ## Why this card exists at all
 *
 * The caption UTM stamper stamps the first link it finds in a caption. Instagram
 * feed captions do not render clickable links. Prod agrees: 0 of 27
 * `traffic_sources_daily` rows and 0 of 24 `order_attribution` rows have ever
 * carried a Mora token. So the mechanism the product was built on cannot work on
 * the platform the product is built for.
 *
 * A discount code is typed at checkout instead of clicked in a caption, so it
 * works everywhere — Instagram, a Story, a printed flyer, a podcast read — and
 * Shopify records the code ON the order, which means the match needs no tracking
 * at all. That is the deterministic half. The bio link is the other half, for
 * merchants who would rather not give away margin.
 *
 * ## Why the copy names the method
 *
 * Triple Whale sells one blended number. This card says which orders were
 * RECORDED at checkout and which were inferred from where a visit came from,
 * because those two things have genuinely different confidence and a merchant
 * deciding where to spend next month deserves to know which is which.
 *
 * ## Six states, and why none of them is a spinner that resolves into nothing
 *
 *  0. we could not find out  → says so, and offers "Try again".
 *  1. no Shopify connection → renders nothing (a store card on an account with
 *     no store is noise; mirrors WhosBuyingSection.tsx:68).
 *  2. permission missing    → the real grant URL, as a button, not an error toast.
 *  3. no code yet           → the percentage row + one CTA.
 *  4. code exists           → the code, the bio link, "Check for sales", and a
 *     timestamp proving the check ran.
 *  5. last attempt failed   → Shopify's own message, verbatim, plus "Try again".
 *
 * 🔴 STATE 0 IS THE ONE THIS DOCSTRING USED TO DENY EXISTED. The preflight runs
 * `retry: false`, so any error leaves `preflight.data` `undefined` — and the old
 * `if (!preflight.data?.hasShopify) return null` made a failed request
 * PIXEL-IDENTICAL to "this account has no store". A merchant who does have
 * Shopify, on one 500 or one dropped connection, watched the card vanish with no
 * message and no next step: exactly the silent sixth state the list above
 * promised did not exist. `null` is now reserved for the one case we positively
 * know: the preflight answered, and it said there is no store.
 *
 * The attribution query is folded into the same state for a sharper reason than
 * symmetry: on error it resolves to `EMPTY`, which reads as "no discount code
 * yet" and offers "Create a discount code" — pressing it would mint a SECOND
 * live code in the merchant's store for a post that already has one.
 *
 * ## Why this card stays PUBLISHED-ONLY even though a code is best pre-publish
 *
 * State 4 used to say "Paste this into your caption", which nobody reading it
 * could do — the card only mounts on published posts. The considered
 * alternative was to also mount it on drafts so the paste is real. It was
 * rejected, and the reason is structural rather than aesthetic: three of this
 * card's four affordances have no meaning before publish.
 * `social_post_targets` rows do not exist until a post publishes, so
 * `data.targets` is empty on every draft and "Point my bio link here" would
 * error every time it was pressed; "Check for sales" would query a post no
 * customer has seen; and the attribution counts would be structurally zero.
 * Mounting on drafts would trade one wrong sentence for three dead buttons —
 * a bigger instance of the same defect, which is the fix telling on itself.
 *
 * So the copy moved to match the capability. The genuinely right home for
 * "put this code in the caption before it goes out" is the COMPOSER, where the
 * caption is still editable — a separate surface, not a wider gate on this one.
 */

interface PostRevenueCardProps {
  postId: string;
  className?: string;
}

const PERCENT_OPTIONS = [0.05, 0.1, 0.15];

export const PostRevenueCard: React.FC<PostRevenueCardProps> = ({ postId, className }) => {
  const preflight = usePostAttributionPreflight();
  const { data, isLoading, isError, error, refetch } = usePostAttribution(postId);
  const createCode = useCreatePostDiscountCode(postId);
  const reconcile = useReconcilePostSales(postId);
  const pinBioLink = usePinBioLink(postId);
  const { isPreviewAccount, message: previewMessage } = usePreviewAccount();

  const [choosing, setChoosing] = useState(false);
  const [percentage, setPercentage] = useState<number | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  const chosen = percentage ?? preflight.data?.defaultValue ?? 0.1;

  const copy = useMemo(
    () =>
      buildProvenanceCopy({
        codeMatched: data.codeMatched,
        tokenMatched: data.tokenMatched,
        total: data.total,
        totalRevenueCents: data.totalRevenueCents,
      }),
    [data.codeMatched, data.tokenMatched, data.total, data.totalRevenueCents],
  );

  const handleCopy = async (value: string, kind: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Your browser blocked the copy. Select the text and copy it manually.');
    }
  };

  const handleCreate = () => {
    createCode.mutate(chosen, {
      onSuccess: (result) => {
        setChoosing(false);
        toast.success(`${result.code} is live in your Shopify store.`);
      },
      onError: (error: Error) => toast.error(error.message),
    });
  };

  const handleReconcile = () => {
    reconcile.mutate(undefined, {
      onSuccess: (result) => {
        toast.success(
          result.matched && result.matched > 0
            ? `Found ${result.matched} ${result.matched === 1 ? 'order' : 'orders'} using this code.`
            : 'No sales have used this code yet.',
        );
      },
      onError: (error: Error) => toast.error(error.message),
    });
  };

  const handlePin = () => {
    const instagram = data.targets.find((t) => t.platform === 'instagram') ?? data.targets[0];
    if (!instagram) {
      toast.error('This post has no published destination to point a bio link at.');
      return;
    }
    pinBioLink.mutate(instagram.connectionId, {
      onError: (error: Error) => toast.error(error.message),
    });
  };

  if (preflight.isLoading || isLoading) {
    return (
      <Card className={cn(ANALYTICS_CARD, 'min-w-0', className)}>
        <CardHeader className={cn(ANALYTICS_CARD_HEADER, 'space-y-0')}>
          <h3 className="text-sm font-medium text-foreground">Sales from this post</h3>
        </CardHeader>
        <CardContent className={cn(ANALYTICS_CARD_BODY, 'space-y-2')}>
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-8 w-40" />
        </CardContent>
      </Card>
    );
  }

  // State 0 — we could not find out. MUST come before the `hasShopify` check:
  // an errored preflight has no `data` at all, so testing `hasShopify` first is
  // what made a failure indistinguishable from an account with no store.
  // Shaped like state 2 (sentence, reason, one button) rather than as an
  // `<Empty>` — this card's own vocabulary for "here is a thing you can do
  // about it" is a paragraph and a button, and a second empty-state system
  // inside one card is the drift the design system calls the #1 smell.
  if (preflight.isError || isError) {
    const detail = (preflight.error ?? error)?.message?.trim();
    return (
      <Card className={cn(ANALYTICS_CARD, 'min-w-0', className)}>
        <CardHeader className={cn(ANALYTICS_CARD_HEADER, 'space-y-0')}>
          <h3 className="text-sm font-medium text-foreground">Sales from this post</h3>
        </CardHeader>
        <CardContent className={cn(ANALYTICS_CARD_BODY, 'space-y-3')}>
          <p className="max-w-2xl text-sm leading-relaxed text-foreground">
            Mora couldn&apos;t reach your Shopify store just now.
          </p>
          <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
            Nothing about your store, this post or any discount code has changed — Mora just
            couldn&apos;t read them. Try again in a moment.
          </p>
          {/* The raw message, verbatim and quiet. Same principle as the Shopify
              `lastError` below: a merchant reporting "Edge Function returned a
              non-2xx status code" gets help faster than one reporting
              "it didn't work", and inventing a friendlier sentence would be
              inventing a diagnosis we do not have. */}
          {detail ? (
            <p className="max-w-2xl break-words text-detail leading-relaxed text-muted-foreground/80">
              {detail}
            </p>
          ) : null}
          <Button
            size="sm"
            onClick={() => {
              if (preflight.isError) void preflight.refetch();
              if (isError) void refetch();
            }}
            disabled={preflight.isFetching}
          >
            <ArrowClockwiseIcon
              className={cn('size-3.5', preflight.isFetching && 'animate-spin')}
              weight="regular"
            />
            {preflight.isFetching ? 'Trying again…' : 'Try again'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // State 1 — no store, POSITIVELY known (the preflight answered). Not an empty
  // state: an account with no Shopify has nothing to say here and a card
  // explaining that is noise.
  if (!preflight.data?.hasShopify) return null;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <h3 className="text-sm font-medium text-foreground">Sales from this post</h3>
      {data.discountCode && preflight.data.adminDiscountsUrl ? (
        <a
          href={preflight.data.adminDiscountsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-detail text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          View in Shopify
          <ArrowSquareOutIcon className="size-3" weight="regular" />
        </a>
      ) : null}
    </div>
  );

  // State 2 — permission missing. A real path, not a toast: the merchant can
  // finish the job from here in one click.
  if (!preflight.data.hasWriteDiscounts) {
    return (
      <Card className={cn(ANALYTICS_CARD, 'min-w-0', className)}>
        <CardHeader className={cn(ANALYTICS_CARD_HEADER, 'space-y-0')}>{header}</CardHeader>
        <CardContent className={cn(ANALYTICS_CARD_BODY, 'space-y-3')}>
          <p className="max-w-2xl text-sm leading-relaxed text-foreground">
            Mora needs permission to create discount codes in your store.
          </p>
          <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
            Giving a post its own code is the only way to know a sale came from it on Instagram,
            where links in captions aren&apos;t clickable. Mora never creates a code without you
            choosing the amount first.
          </p>
          {preflight.data.installUrl ? (
            <Button asChild size="sm">
              <a href={preflight.data.installUrl} target="_blank" rel="noreferrer">
                Grant permission
              </a>
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const body = () => {
    // State 3 — no code yet.
    if (!data.discountCode) {
      return (
        <div className="space-y-3">
          <p className="max-w-2xl text-sm leading-relaxed text-foreground">
            Give this post its own discount code. When someone uses it at checkout, Shopify tells us
            the sale came from here — even on Instagram, where links in captions aren&apos;t
            clickable.
          </p>

          {/* State 5 — the last attempt failed. Shopify's own wording, kept
              rather than replaced: a merchant can act on "Code must be unique",
              and cannot act on "Something went wrong". */}
          {data.lastError ? (
            <p className="max-w-2xl text-detail leading-relaxed text-destructive">
              Shopify refused the last attempt: {data.lastError}
            </p>
          ) : null}

          {isPreviewAccount ? (
            <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
              {previewMessage}
            </p>
          ) : choosing ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {PERCENT_OPTIONS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPercentage(value)}
                    className={cn(
                      SELECTABLE_PILL,
                      chosen === value ? SELECTABLE_PILL_ON : SELECTABLE_PILL_OFF,
                    )}
                  >
                    {Math.round(value * 100)}% off
                  </button>
                ))}
              </div>
              {/* Every guard that protects the merchant's margin, stated BEFORE
                  the button rather than discovered afterwards. */}
              <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
                One use per customer, expires in 30 days, and it won&apos;t stack with your other
                discounts.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={handleCreate} disabled={createCode.isPending}>
                  {createCode.isPending
                    ? 'Creating in Shopify…'
                    : `Create a ${Math.round(chosen * 100)}% code`}
                </Button>
                <Button
                  variant="flat"
                  size="sm"
                  onClick={() => setChoosing(false)}
                  disabled={createCode.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" onClick={() => setChoosing(true)}>
              {data.lastError ? 'Try again' : 'Create a discount code'}
            </Button>
          )}
        </div>
      );
    }

    // State 4 — the code exists.
    const code = data.discountCode;
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-1 font-mono text-sm tracking-wide text-foreground">
            {code.code}
          </span>
          <Button variant="flat" size="sm" onClick={() => handleCopy(code.code, 'code')}>
            {copied === 'code' ? (
              <CheckIcon className="size-3.5" weight="regular" />
            ) : (
              <CopyIcon className="size-3.5" weight="regular" />
            )}
            {copied === 'code' ? 'Copied' : 'Copy'}
          </Button>
          <span className="text-detail text-muted-foreground">
            {Math.round(code.percentage * 100)}% off
            {code.endsAt ? ` · expires ${format(new Date(code.endsAt), 'MMM d')}` : ''}
          </span>
        </div>

        {/* 🔴 THIS LINE USED TO READ "Paste this into your caption." — an
            instruction nobody reading it could carry out. This card renders
            only on published posts (`PostBody.tsx:3853`, `isLiveOrPartial`),
            and a published caption cannot be edited from Mora on any platform
            it publishes to. The copy now names the places a live post's code
            can still reach. */}
        <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
          This post is already live, so add the code where you still can — a comment on the post, a
          Story, or your bio. Sales that use it show up here.
        </p>

        {copy.headline ? (
          <>
            <p className="max-w-2xl text-sm leading-relaxed text-foreground">{copy.headline}</p>
            {copy.provenance ? (
              <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
                {copy.provenance}
              </p>
            ) : null}
          </>
        ) : (
          <p className="max-w-2xl text-sm leading-relaxed text-foreground">
            No sales have used this code yet.
          </p>
        )}

        {/* The bio link. Second, not first: it asks the merchant to replace
            whatever is already in their bio, which is a real behaviour change,
            whereas the code asks them to paste nine characters. */}
        <div className="space-y-2 border-t border-border/60 pt-3">
          {data.bioLink?.pinnedToThisPost ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <LinkIcon className="size-3.5 shrink-0 text-muted-foreground" weight="regular" />
                <span className="min-w-0 break-all font-mono text-detail text-foreground">
                  {bioLinkUrl(data.bioLink.slug)}
                </span>
                <Button
                  variant="flat"
                  size="sm"
                  onClick={() => handleCopy(bioLinkUrl(data.bioLink!.slug), 'link')}
                >
                  {copied === 'link' ? (
                    <CheckIcon className="size-3.5" weight="regular" />
                  ) : (
                    <CopyIcon className="size-3.5" weight="regular" />
                  )}
                  {copied === 'link' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
                One link in your bio, now pointing at this post.{' '}
                {buildTapSentence(data.bioClicks, data.bioLastToken, data.bioTopReferrerHost)}
              </p>
              {data.bioLastDestination ? (
                <p className="max-w-2xl break-all text-detail leading-relaxed text-muted-foreground/80">
                  Last tap went to {data.bioLastDestination}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <Button variant="flat" size="sm" onClick={handlePin} disabled={pinBioLink.isPending}>
                <LinkIcon className="size-3.5" weight="regular" />
                {pinBioLink.isPending ? 'Pointing…' : 'Point my bio link here'}
              </Button>
              <p className="max-w-2xl text-detail leading-relaxed text-muted-foreground">
                One Mora link in your profile bio that always sends people to the post you choose —
                no discount needed.
              </p>
            </>
          )}
        </div>

        {/* E5: the timestamp IS the evidence that something ran. Without it a
            check is indistinguishable from a spinner that resolved into the
            same screen. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/60 pt-3">
          <Button variant="flat" size="sm" onClick={handleReconcile} disabled={reconcile.isPending}>
            <ArrowClockwiseIcon
              className={cn('size-3.5', reconcile.isPending && 'animate-spin')}
              weight="regular"
            />
            {reconcile.isPending ? 'Checking Shopify…' : 'Check for sales'}
          </Button>
          <span className="text-detail text-muted-foreground/80">
            {buildCheckedSentence(data.lastCheckedAt)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Card className={cn(ANALYTICS_CARD, 'min-w-0', className)}>
      <CardHeader className={cn(ANALYTICS_CARD_HEADER, 'space-y-0')}>{header}</CardHeader>
      <CardContent className={cn(ANALYTICS_CARD_BODY, 'space-y-3')}>{body()}</CardContent>
    </Card>
  );
};
